import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { formatPrice, formatPercentage } from '@/lib/utils';
import { AdminNavigation } from '@/components/AdminNavigation';
import { 
  TrendingUp, DollarSign, Users, Calendar, Download,
  CheckCircle, Clock, AlertCircle, CreditCard,
  FileText, Calculator, RefreshCw, Banknote, Copy
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

interface RebateSummaryResponse {
  period: string;
  periodType: 'quarterly' | 'annually';
  summary: {
    totalCustomers: number;
    totalPurchases: number;
    totalRebates: number;
    pendingRebates: number;
    approvedRebates: number;
    paidRebates: number;
  };
  rebates: Array<{
    rebate: any;
    customer: {
      id: number;
      businessName: string;
      contactPerson: string;
      email: string;
      phone?: string;
    };
    tier: string;
    discountPercentage: number;
  }>;
}

// Payment Modal Component
function PaymentModal({ 
  rebate, 
  isOpen, 
  onClose 
}: { 
  rebate: any | null;
  isOpen: boolean; 
  onClose: () => void; 
}) {
  const [paymentMethod, setPaymentMethod] = useState<'CHQ' | 'eTransfer'>('CHQ');
  const [paymentReference, setPaymentReference] = useState('');
  const [notes, setNotes] = useState(rebate?.rebate.notes || '');
  const { toast } = useToast();

  const updateMutation = useMutation({
    mutationFn: (data: any) => 
      apiRequest('PATCH', `/api/admin/rebates/${rebate?.rebate.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-rebates'] });
      toast({ title: 'Payment recorded successfully' });
      onClose();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to record payment', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      status: 'paid',
      paidDate: new Date(),
      paymentMethod,
      paymentReference,
      notes
    });
  };

  if (!rebate) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record Rebate Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <p className="font-semibold text-lg">{rebate.customer.businessName}</p>
            <div className="text-sm text-gray-600">
              <p>Period: {rebate.rebate.period}</p>
              <p className="font-medium text-base mt-1">
                Rebate Amount: <span className="text-emerald-600">{formatPrice(rebate.rebate.rebateAmount)}</span>
              </p>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select value={paymentMethod} onValueChange={(value: 'CHQ' | 'eTransfer') => setPaymentMethod(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CHQ">Cheque</SelectItem>
                <SelectItem value="eTransfer">e-Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="reference">
              {paymentMethod === 'CHQ' ? 'Cheque Number' : 'e-Transfer Reference'}
            </Label>
            <Input
              id="reference"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder={paymentMethod === 'CHQ' ? 'Enter cheque number' : 'Enter e-Transfer reference'}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional payment details..."
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Record Payment
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Rebate Report Generator
function RebateReportGenerator({ customerId, customerName, period }: { 
  customerId: number; 
  customerName: string;
  period: string;
}) {
  const { toast } = useToast();
  
  const generateReportContent = async () => {
    // Get rebate summary
    const response = await apiRequest(`/api/admin/rebates/customer/${customerId}/report?period=${period}`);
    
    // Get order-by-order details
    const orderResponse = await apiRequest(`/api/admin/rebates/customer/${customerId}/orders?year=${period.split('-')[0]}`);
    
    // Format order details
    let orderDetails = '';
    if (orderResponse.orders && orderResponse.orders.length > 0) {
      orderDetails = `
ORDER-BY-ORDER BREAKDOWN
========================

${'Date'.padEnd(12)} ${'Order #'.padEnd(15)} ${'Amount'.padEnd(10)} ${'Discount'.padEnd(10)} ${'Rebate'.padEnd(10)}
${'='.repeat(12)} ${'='.repeat(15)} ${'='.repeat(10)} ${'='.repeat(10)} ${'='.repeat(10)}
`;
      
      orderResponse.orders.forEach((order: any) => {
        const date = new Date(order.orderDate).toLocaleDateString();
        const orderNum = order.orderNumber.substring(0, 14);
        const amount = formatPrice(order.orderAmount);
        const discount = order.discountCode || 'None';
        const rebate = formatPrice(order.rebateAmount);
        
        orderDetails += `${date.padEnd(12)} ${orderNum.padEnd(15)} ${amount.padEnd(10)} ${discount.padEnd(10)} ${rebate.padEnd(10)}\n`;
      });
      
      orderDetails += `${'='.repeat(12)} ${'='.repeat(15)} ${'='.repeat(10)} ${'='.repeat(10)} ${'='.repeat(10)}\n`;
      orderDetails += `${'TOTALS:'.padEnd(42)} ${formatPrice(orderResponse.summary.totalPurchases).padEnd(10)} ${formatPrice(orderResponse.summary.totalRebates).padEnd(10)}\n`;
    }
    
    // Create comprehensive report
    return `CUSTOMER REBATE STATEMENT
=========================

Customer Information:
- Business Name: ${customerName}
- Period: ${period}
- Generated: ${new Date().toLocaleDateString()}
- Status: ${response.status}

REBATE SUMMARY
==============
Total Purchases: ${formatPrice(response.totalPurchases)}
Rebate Percentage: ${formatPercentage(response.rebatePercentage * 100)}
Total Rebate Amount: ${formatPrice(response.rebateAmount)}

Payment Information:
${response.paidDate ? `- Paid Date: ${new Date(response.paidDate).toLocaleDateString()}` : '- Status: Pending Payment'}
${response.paymentMethod ? `- Payment Method: ${response.paymentMethod}` : ''}
${response.paymentReference ? `- Reference: ${response.paymentReference}` : ''}
${response.notes ? `- Notes: ${response.notes}` : ''}
${orderDetails}

TERMS & CONDITIONS
==================
- Rebates are calculated based on total purchase volume during the period
- Rebate percentage is determined by customer tier level
- All amounts are in CAD
- For questions, please contact our accounts department

Thank you for your continued business!

---
Report generated on ${new Date().toLocaleString()}`;
  };

  const downloadReport = async () => {
    try {
      const reportContent = await generateReportContent();
      
      // Download as text file
      const blob = new Blob([reportContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rebate-statement-${customerName.replace(/\s+/g, '-')}-${period}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({ title: 'Rebate report downloaded successfully' });
    } catch (error: any) {
      toast({ 
        title: 'Failed to generate report', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  };

  const copyToClipboard = async () => {
    try {
      const reportContent = await generateReportContent();
      await navigator.clipboard.writeText(reportContent);
      toast({ title: 'Report copied to clipboard - ready to paste!' });
    } catch (error: any) {
      toast({ 
        title: 'Failed to copy report', 
        description: error.message,
        variant: 'destructive' 
      });
    }
  };
  
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={copyToClipboard}>
        <Copy className="w-4 h-4 mr-1" />
        Copy Report
      </Button>
      <Button size="sm" variant="outline" onClick={downloadReport}>
        <FileText className="w-4 h-4 mr-1" />
        Download
      </Button>
    </div>
  );
}

export default function AdminRebates() {
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor((new Date().getMonth() + 3) / 3);
  
  const [periodType, setPeriodType] = useState<'quarterly' | 'annually'>('quarterly');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);
  const [selectedRebate, setSelectedRebate] = useState<any>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const { toast } = useToast();
  
  // Calculate selected period based on year and quarter/annual selection
  const selectedPeriod = useMemo(() => {
    if (periodType === 'quarterly') {
      return `${selectedYear}-Q${selectedQuarter}`;
    } else {
      return selectedYear.toString();
    }
  }, [periodType, selectedYear, selectedQuarter]);

  // Fetch rebate summary
  const { data: summary, isLoading, refetch } = useQuery<RebateSummaryResponse>({
    queryKey: ['admin-rebates', periodType, selectedPeriod],
    queryFn: () => apiRequest(`/api/admin/rebates/summary?periodType=${periodType}&period=${selectedPeriod}`),
  });

  // Calculate rebates mutation
  const calculateRebatesMutation = useMutation({
    mutationFn: () => 
      apiRequest('POST', '/api/admin/rebates/calculate', {
        periodType,
        period: selectedPeriod
      }),
    onSuccess: () => {
      // Invalidate and refetch to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['admin-rebates'] });
      refetch();
      toast({ title: 'Rebates calculated successfully' });
      setIsCalculating(false);
    },
    onError: (error: any) => {
      toast({ 
        title: 'Failed to calculate rebates', 
        description: error.message,
        variant: 'destructive' 
      });
      setIsCalculating(false);
    }
  });

  const handleCalculateRebates = () => {
    setIsCalculating(true);
    calculateRebatesMutation.mutate();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'below_minimum': return 'bg-red-100 text-red-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'approved': return <Clock className="w-4 h-4" />;
      case 'below_minimum': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  // Generate year options dynamically (from current year + 10 down to 2024)
  const yearOptions = useMemo(() => {
    const options = [];
    const maxYear = currentYear + 10; // Allow future years
    for (let year = maxYear; year >= 2024; year--) {
      options.push(year);
    }
    return options;
  }, [currentYear]);
  
  // Generate quarter options with availability check
  const quarterOptions = useMemo(() => {
    const options = [];
    for (let q = 1; q <= 4; q++) {
      // Only show quarters that have passed or are current (unless it's a future year)
      if (selectedYear > currentYear || (selectedYear === currentYear && q <= currentQuarter) || selectedYear < currentYear) {
        options.push(q);
      }
    }
    return options;
  }, [selectedYear, currentYear, currentQuarter]);
  
  // Auto-adjust quarter when year changes
  useEffect(() => {
    if (selectedYear === currentYear && selectedQuarter > currentQuarter) {
      setSelectedQuarter(currentQuarter);
    } else if (selectedYear > currentYear) {
      setSelectedQuarter(1); // Default to Q1 for future years
    }
  }, [selectedYear, currentYear, currentQuarter, selectedQuarter]);

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNavigation />

      <div className="container mx-auto p-4 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Rebate Management</h1>
            <p className="text-gray-600 mt-1">Calculate and track customer rebates based on purchase volume</p>
          </div>
          <Button 
            onClick={handleCalculateRebates}
            disabled={isCalculating}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isCalculating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="w-4 h-4 mr-2" />
                Calculate Rebates
              </>
            )}
          </Button>
        </div>

        {/* Period Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Period Selection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select value={periodType} onValueChange={(value: 'quarterly' | 'annually') => {
                  setPeriodType(value);
                }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {periodType === 'quarterly' && (
                <div className="space-y-2">
                  <Label>Quarter</Label>
                  <Select value={selectedQuarter.toString()} onValueChange={(value) => setSelectedQuarter(parseInt(value))}>
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {quarterOptions.map(quarter => (
                        <SelectItem key={quarter} value={quarter.toString()}>
                          Q{quarter}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm text-gray-600">Selected Period</Label>
                <div className="px-3 py-2 bg-gray-50 border rounded-md text-sm font-medium">
                  {selectedPeriod}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Sales</p>
                    <p className="text-2xl font-bold">{formatPrice(summary.summary.totalPurchases || 0)}</p>
                  </div>
                  <DollarSign className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Rebates</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatPrice(summary.summary.totalRebates || 0)}
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Customers</p>
                    <p className="text-2xl font-bold">{summary.summary.totalCustomers || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Paid Out</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatPrice(summary.summary.paidRebates || 0)}
                    </p>
                  </div>
                  <Banknote className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Customer Rebates Table */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Rebates - {selectedPeriod}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
              </div>
            ) : summary?.rebates && summary.rebates.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Customer</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Contact</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Total Purchases</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Tier</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Rebate</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Payment</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {summary.rebates.map((item) => (
                      <tr key={item.rebate.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium">{item.customer.businessName}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <div>{item.customer.contactPerson}</div>
                            <div className="text-gray-600">{item.customer.email}</div>
                            {item.customer.phone && (
                              <div className="text-gray-600">{item.customer.phone}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {formatPrice(item.rebate.totalPurchases)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className="bg-purple-100 text-purple-800">
                            {item.tier}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div>
                            <div className="font-medium text-emerald-600">
                              {formatPrice(item.rebate.rebateAmount)}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatPercentage(item.rebate.rebatePercentage * 100, 1)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={getStatusColor(item.rebate.status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(item.rebate.status)}
                              {item.rebate.status}
                            </span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {item.rebate.status === 'paid' && (
                            <div className="text-sm">
                              <div className="font-medium">{item.rebate.paymentMethod}</div>
                              {item.rebate.paymentReference && (
                                <div className="text-gray-600">#{item.rebate.paymentReference}</div>
                              )}
                              {item.rebate.paidDate && (
                                <div className="text-gray-600">
                                  {new Date(item.rebate.paidDate).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end">
                            <RebateReportGenerator 
                              customerId={item.customer.id}
                              customerName={item.customer.businessName}
                              period={selectedPeriod}
                            />
                            {item.rebate.status !== 'paid' && (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                  setSelectedRebate(item);
                                  setIsPaymentModalOpen(true);
                                }}
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                Record Payment
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p>No rebate data for {selectedPeriod}</p>
                <p className="text-sm mt-2">Click "Calculate Rebates" to generate rebates for this period</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        rebate={selectedRebate}
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedRebate(null);
        }}
      />
    </div>
  );
}